import type { EditorContext } from "../tools/EditorContext";
import type { SceneSelection } from "../canvas/Scene";
import { pickAdjacentDecoration } from "./projectOps";
import {
  descendantConstructionGroupIds,
  pieceStitchLines,
  projectConstructionGroups,
  projectConstructionLines,
  projectTracingImages,
  type ConstructionGroup,
} from "./Project";
import { descendantLeafIds } from "./ConstructionObject";
import { pruneConstraintsForDeletedLines } from "./constraints";

/** True when `sel` points at something `deleteSelection` can remove. Drives
 *  both the canvas Delete shortcut's early-out and the Parts panel trashcan's
 *  enabled state so the two never disagree about whether there's anything to
 *  delete. Stitching-scope selections (a settings block, not a concrete
 *  item) are intentionally excluded — same as the Delete shortcut. */
export function hasDeletableSelection(sel: SceneSelection): boolean {
  return (
    sel.pieceId != null ||
    (sel.pieceIds?.length ?? 0) > 0 ||
    sel.decorationId != null ||
    sel.holeId != null ||
    sel.cutoutId != null ||
    sel.stitchId != null ||
    sel.constructionId != null ||
    (sel.constructionIds?.length ?? 0) > 0 ||
    (sel.constructionGroupIds?.length ?? 0) > 0 ||
    (sel.tracingImageIds?.length ?? 0) > 0
  );
}

/** Delete whatever is currently selected, in one history commit, then move
 *  the selection to a sensible neighbour. Shared by the canvas Delete /
 *  Backspace shortcut and the Parts panel trashcan so both routes behave
 *  identically across every selectable item type (parts, decorations,
 *  holes, cutouts, stitch lines, and construction lines / groups).
 *
 *  The branch order is a priority chain: when a sub-item of a part is the
 *  focus it's deleted in preference to the part itself, matching what the
 *  user is looking at. Returns true when something was deleted. */
export function deleteSelection(ctx: EditorContext): boolean {
  const sel = ctx.scene.getSelection();
  if (sel.decorationId && sel.pieceId) {
    const pieceId = sel.pieceId;
    const decorationId = sel.decorationId;
    const piece = ctx.history.state.pieces.find((p) => p.id === pieceId);
    const nextDecorationId = piece
      ? pickAdjacentDecoration(piece, decorationId)
      : null;
    ctx.commit("Delete decoration", (proj) => ({
      ...proj,
      pieces: proj.pieces.map((pc) =>
        pc.id === pieceId
          ? {
              ...pc,
              decorations: pc.decorations.filter((d) => d.id !== decorationId),
            }
          : pc,
      ),
    }));
    ctx.setSelection({ pieceId, decorationId: nextDecorationId });
    return true;
  } else if (sel.holeId && sel.pieceId) {
    const pieceId = sel.pieceId;
    const holeId = sel.holeId;
    const piece = ctx.history.state.pieces.find((p) => p.id === pieceId);
    const idx = piece?.holes.findIndex((h) => h.id === holeId) ?? -1;
    const nextHoleId =
      piece && idx >= 0
        ? (piece.holes[idx + 1]?.id ?? piece.holes[idx - 1]?.id ?? null)
        : null;
    ctx.commit("Delete hole", (proj) => ({
      ...proj,
      pieces: proj.pieces.map((pc) =>
        pc.id === pieceId
          ? { ...pc, holes: pc.holes.filter((h) => h.id !== holeId) }
          : pc,
      ),
    }));
    ctx.setSelection({ pieceId, decorationId: null, holeId: nextHoleId });
    return true;
  } else if (sel.cutoutId && sel.pieceId) {
    const pieceId = sel.pieceId;
    const cutoutId = sel.cutoutId;
    const piece = ctx.history.state.pieces.find((p) => p.id === pieceId);
    const idx = piece?.cutouts.findIndex((c) => c.id === cutoutId) ?? -1;
    const nextCutoutId =
      piece && idx >= 0
        ? (piece.cutouts[idx + 1]?.id ?? piece.cutouts[idx - 1]?.id ?? null)
        : null;
    ctx.commit("Delete cutout", (proj) => ({
      ...proj,
      pieces: proj.pieces.map((pc) =>
        pc.id === pieceId
          ? { ...pc, cutouts: pc.cutouts.filter((c) => c.id !== cutoutId) }
          : pc,
      ),
    }));
    ctx.setSelection({ pieceId, decorationId: null, cutoutId: nextCutoutId });
    return true;
  } else if (
    sel.constructionId ||
    (sel.constructionIds && sel.constructionIds.length > 0) ||
    (sel.constructionGroupIds && sel.constructionGroupIds.length > 0) ||
    (sel.tracingImageIds && sel.tracingImageIds.length > 0)
  ) {
    // Issue #283: delete every selected construction line AND every line
    // transitively inside any selected construction group. Previously only
    // `sel.constructionId` (the lead single-select id) was deleted, so a
    // multi-line or group selection silently lost only one member.
    // Tracing images are construction objects too — delete every selected
    // image AND every image inside a selected group.
    const project = ctx.history.state;
    const explicitLineIds =
      sel.constructionIds && sel.constructionIds.length > 0
        ? sel.constructionIds
        : sel.constructionId
          ? [sel.constructionId]
          : [];
    const selectedGroupIds = sel.constructionGroupIds ?? [];
    const lineIdSet = new Set<string>(explicitLineIds);
    const imageIdSet = new Set<string>(sel.tracingImageIds ?? []);
    const groupIdSet = new Set<string>();
    for (const gid of selectedGroupIds) {
      groupIdSet.add(gid);
      for (const sub of descendantConstructionGroupIds(project, gid)) {
        groupIdSet.add(sub);
      }
      const members = descendantLeafIds(project, gid);
      for (const lid of members.lineIds) lineIdSet.add(lid);
      for (const iid of members.imageIds) imageIdSet.add(iid);
    }
    if (lineIdSet.size === 0 && groupIdSet.size === 0 && imageIdSet.size === 0) {
      return false;
    }
    // Pick a "next selected line" from outside the about-to-delete set,
    // anchored on the lead's position in project order — same pattern the
    // single-id branch used so a quick repeated Delete keeps stepping
    // through neighbours.
    const allLines = projectConstructionLines(project);
    const leadId =
      sel.constructionId ??
      explicitLineIds[explicitLineIds.length - 1] ??
      null;
    const leadIdx = leadId ? allLines.findIndex((l) => l.id === leadId) : -1;
    let nextConstructionId: string | null = null;
    if (leadIdx >= 0) {
      for (let i = leadIdx + 1; i < allLines.length; i++) {
        if (!lineIdSet.has(allLines[i]!.id)) {
          nextConstructionId = allLines[i]!.id;
          break;
        }
      }
      if (!nextConstructionId) {
        for (let i = leadIdx - 1; i >= 0; i--) {
          if (!lineIdSet.has(allLines[i]!.id)) {
            nextConstructionId = allLines[i]!.id;
            break;
          }
        }
      }
    }
    ctx.commit("Delete construction selection", (proj) => ({
      ...proj,
      constructionLines: projectConstructionLines(proj).filter(
        (l) => !lineIdSet.has(l.id),
      ),
      // Remove deleted tracing images from the project collection.
      tracingImages: projectTracingImages(proj).filter(
        (img) => !imageIdSet.has(img.id),
      ),
      // Strip the deleted groups AND scrub dangling child-group references
      // from any surviving parent (defensive — the descendant walk above
      // should already cover every nested member, but a corrupted
      // cross-tree reference shouldn't leave a ghost behind). Mirrors the
      // layers-panel `deleteConstructionGroup` cleanup. Deleted lines and
      // images are also stripped from surviving groups' member arrays.
      constructionGroups: projectConstructionGroups(proj)
        .filter((g) => !groupIdSet.has(g.id))
        .map((g) => {
          const groupIds = (g.groupIds ?? []).filter(
            (gid) => !groupIdSet.has(gid),
          );
          const imageIds = (g.imageIds ?? []).filter(
            (id) => !imageIdSet.has(id),
          );
          const next: ConstructionGroup = {
            ...g,
            lineIds: g.lineIds.filter((id) => !lineIdSet.has(id)),
          };
          next.groupIds = groupIds.length > 0 ? groupIds : undefined;
          next.imageIds = imageIds.length > 0 ? imageIds : undefined;
          return next;
        }),
      constraints: pruneConstraintsForDeletedLines(proj.constraints, lineIdSet),
    }));
    ctx.setSelection({
      pieceId: null,
      decorationId: null,
      constructionId: nextConstructionId,
      constructionIds: nextConstructionId ? [nextConstructionId] : [],
      constructionGroupIds: [],
      tracingImageIds: [],
    });
    return true;
  } else if (sel.stitchId && sel.pieceId) {
    const pieceId = sel.pieceId;
    const stitchId = sel.stitchId;
    const piece = ctx.history.state.pieces.find((p) => p.id === pieceId);
    const lines = piece ? pieceStitchLines(piece) : [];
    const idx = lines.findIndex((s) => s.id === stitchId);
    const nextStitchId =
      idx >= 0 ? (lines[idx + 1]?.id ?? lines[idx - 1]?.id ?? null) : null;
    ctx.commit("Delete stitch line", (proj) => ({
      ...proj,
      pieces: proj.pieces.map((pc) =>
        pc.id === pieceId
          ? {
              ...pc,
              stitchLines: pieceStitchLines(pc).filter(
                (s) => s.id !== stitchId,
              ),
            }
          : pc,
      ),
    }));
    ctx.setSelection({ pieceId, decorationId: null, stitchId: nextStitchId });
    return true;
  } else if (sel.pieceId) {
    // Multi-piece selection (issue #251) — delete every selected part in
    // one commit. Falls back to single-piece delete when `pieceIds` is the
    // legacy [pieceId] single-set.
    const pieceIds =
      sel.pieceIds && sel.pieceIds.length > 0 ? sel.pieceIds : [sel.pieceId];
    const idSet = new Set(pieceIds);
    ctx.commit(
      pieceIds.length > 1 ? "Delete parts" : "Delete part",
      (proj) => ({
        ...proj,
        pieces: proj.pieces.filter((p) => !idSet.has(p.id)),
      }),
    );
    ctx.setSelection({ pieceId: null, decorationId: null });
    return true;
  }
  return false;
}
